import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { NavListCommonProps } from 'src/app/components/nav/nav.component';
import { environment } from 'src/environments/environment';
import { SiteMapUrl } from '../_enums/sitemap.url';
import { Role } from '../_models/role';
import { User } from '../_models/user';
import { UserRight } from '../_models/userRight';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private router: Router,
    private translate: TranslateService
  ) {}

  getAll() {
    return this.http.get<User[]>(`${environment.apiUrl}/users`, { headers: this.authService.headers });
  }

  getUsersEcoPro() {
    return this.http.get<User[]>(`${environment.apiUrl}/users/ecopro`, { headers: this.authService.headers });
  }

  update(user: User) {
    return this.http.put<User>(`${environment.apiUrl}/users/${user.userId}`, user, { headers: this.authService.headers }).toPromise();
  }

  add(user: User) {
    return this.http.post<any>(`${environment.apiUrl}/users`, user, { headers: this.authService.headers }).toPromise();
  }

  addEcoPro(user: User) {
    return this.http.post<any>(`${environment.apiUrl}/users/ecopro`, user, { headers: this.authService.headers }).toPromise();
  }

  activateEcoPro(user: User) {
    return this.http.put<any>(`${environment.apiUrl}/users/ecopro/${user.userId}`, user, { headers: this.authService.headers });
  }

  delete(userId: string) {
    return this.http.delete(`${environment.apiUrl}/users/${userId}`, { headers: this.authService.headers });
  }

  deactivateEcoPro(user: User) {
    return this.http.put<User>(`${environment.apiUrl}/users/ecopro/deactivate/${user.userId}`, user, { headers: this.authService.headers });
  }

  get(userId: string) {
    return this.http.get<User>(`${environment.apiUrl}/users/${userId}`, { headers: this.authService.headers });
  }

  getUserEcoProByEmail(email: string) {
    return this.http.get<User>(`${environment.apiUrl}/users/ecopro/email/${email}`, { headers: this.authService.headers });
  }

  emailHasAccount(email: string) {
    return this.http.get<boolean>(`${environment.apiUrl}/users/emailHasAccount?email=${encodeURIComponent(email)}`);
  }

  sendExistingAccounts(email: string) {
    return this.http.post(`${environment.apiUrl}/users/sendExistingAccounts?email=${encodeURIComponent(email)}`, null);
  }

  getRightsUserByPerimeter(perimeterId: string, profileCode: string) {
    return this.http.get<UserRight[]>(`${environment.apiUrl}/users/perimeter/${perimeterId}/rights?profileCode=${profileCode}`, { headers: this.authService.headers }).toPromise();
  }

  deleteUserRight(userId?: string, rightId?: string) {
    return this.http.delete<UserRight>(`${environment.apiUrl}/users/${userId}/right/${rightId}`, { headers: this.authService.headers }).toPromise();
  }

  addUserRight(userId?: string, right?: UserRight) {
    return this.http.post<UserRight>(`${environment.apiUrl}/users/${userId}/right`, right, { headers: this.authService.headers }).toPromise();
  }

  userProfilIsMo(role: Role) {
    return this.http.get<boolean>(`${environment.apiUrl}/users/userProfilIsMO/${role.perimeterType}`, { headers: this.authService.headers });
  }

  getNavConfig({ perimeterId }: NavListCommonProps) {
    return {
      header: {
        title: 'ressource_list_title_users',
        actions: [{ label: 'ressource_list_action_add', onClick: () => this.router.navigate([`${SiteMapUrl.MenuUser}/new`, 0], { state: { perimeterId } }) }],
        filters: []
      },
      getItemConfig: (node: any) => {
        return {
          displayName: `${node.displayName}`,
          url: SiteMapUrl.MenuUser + '/' + node.userId,
          itemType: 'green',
          state: {
            resourceId: node.resourceId,
            perimeterId
          }
        };
      },
      getDataSource: () =>
        this.getAll().pipe(
          map((data: any[]) =>
            data.map((node: any) => ({
              key: node.adherentDisplayName,
              value: node.users.map((user: any) => ({
                userId: user.userId,
                displayName: user.userDisplayName
              }))
            }))
          )
        )
    };
  }
}
